import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/store/store';
import { addTestToFavorites } from '@/store/reducers/myTestsSlice';
import { testFavoriteHydrate, toggleTestFavorite } from '@/store/reducers/testFavoritesSlice';
import { selectIsTestFavorite } from '@/store/selectors/testFavoritesSelectors';
import { reachGoal } from '@/core/utils/metrika';
import localizePath from '@/core/utils/localizePath';
import FavoriteHeartButton from './FavoriteHeartButton';
import Snackbar from '@/components/organisms/Snackbar/Snackbar';
import { TextS } from '@/components/atoms/UI/Typography';
import Button from '@/components/atoms/UI/buttons/Button';
import FlexContainer from "@/components/molecules/Layout/FlexContainer";

interface Props {
    testId: number;
    /** Начальное серверное состояние (для авторизованного пользователя). */
    initialIsFavorite?: boolean;
    /** Залогинен ли пользователь (гость → редирект на форму регистрации). */
    isAuthenticated?: boolean;
    authUrl?: string;
    favoritesUrl?: string;
}

// Тост с CTA — раз за сессию (дальше добавление молча, подтверждение даёт
// анимация сердца). sessionStorage, не local: новая сессия — снова уместно.
const FAVORITE_TOAST_KEY = 'favoriteAddedToastShown';

const favoriteToastAlreadyShown = (): boolean => {
    try {
        return sessionStorage.getItem(FAVORITE_TOAST_KEY) === '1';
    } catch {
        return false;
    }
};

const markFavoriteToastShown = () => {
    try {
        sessionStorage.setItem(FAVORITE_TOAST_KEY, '1');
    } catch {
        /* noop */
    }
};

/**
 * Кнопка «В избранное» для теста — обычный React-компонент.
 *
 * Используется и как island на странице теста (регистрируется в
 * `islands/components/index.ts`), и напрямую внутри React-страниц
 * (например шапка результата — она сама уже island). Поэтому логика
 * живёт здесь, а не в island-обёртке.
 *
 * Бизнес-логика — в Redux: `toggleTestFavorite` → optimistic-thunk'и
 * `myTestsSlice` (API + cross-slice `testFavoriteSet`), на который подписан
 * `testFavoritesSlice`. Здесь только: чтение селектора, гидрация начального
 * состояния из серверного пропа и делегирование по клику. Гость → регистрация.
 */
const TestFavoriteButton: React.FC<Props> = ({
    testId,
    initialIsFavorite = false,
    isAuthenticated = false,
    authUrl = localizePath('/register'),
    favoritesUrl = localizePath('/profile/tests'),
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const isFavorite = useAppSelector((state) => selectIsTestFavorite(state, testId));

    // Триггеры анимации — только по ЯВНОМУ действию (клик/авто-добавление).
    // Гидрация/оптимистичный откат/синк стор не анимируются: это чинит ложный
    // «раскол сердца» при заходе на страницу результата уже-избранного теста.
    const [beatNonce, setBeatNonce] = useState(0);
    const [breakNonce, setBreakNonce] = useState(0);

    // Тост «добавлено в избранное» — раз за сессию (см. FAVORITE_TOAST_KEY).
    const [toastVisible, setToastVisible] = useState(false);
    const showFavoriteToastOncePerSession = () => {
        if (favoriteToastAlreadyShown()) return;
        markFavoriteToastShown();
        setToastVisible(true);
    };

    // Гидрируем серверное начальное состояние в стор — без анимации и БЕЗ
    // затирания уже известного (более свежего) значения: острова делят один
    // store, и результат этого же теста может открыться инлайн после клика по
    // избранному на карточке (см. testFavoriteHydrate).
    useEffect(() => {
        dispatch(testFavoriteHydrate({ id: testId, isFavorite: initialIsFavorite }));
    }, [dispatch, testId, initialIsFavorite]);

    // Подход А: гость нажал «в избранное» → регистрация/логин → возврат сюда с
    // ?add_favorite=1 → авто-добавляем и чистим параметр (чтобы не повторять на
    // рефреше). Гарды: авторизован + ещё не в избранном. Бэк идемпотентен.
    // Бонус: isFavorite станет true → на кнопке проиграет «сердцебиение».
    useEffect(() => {
        if (!isAuthenticated || initialIsFavorite) {
            return;
        }
        const params = new URLSearchParams(window.location.search);
        if (params.get('add_favorite') !== '1') {
            return;
        }

        dispatch(addTestToFavorites(testId));
        setBeatNonce((n) => n + 1); // авто-добавление → «сердцебиение»
        showFavoriteToastOncePerSession();
        reachGoal('test_favorite_add', { testId, source: 'after_auth' });

        params.delete('add_favorite');
        const search = params.toString();
        const cleanUrl = window.location.pathname + (search ? `?${search}` : '') + window.location.hash;
        window.history.replaceState({}, '', cleanUrl);
        // Только на маунте: реагируем на возврат из авторизации, не на каждый рендер.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const label = isFavorite
        ? t('testItem.remove_from_favorites')
        : t('testItem.add_to_favorites');

    const handleClick = () => {
        if (!isAuthenticated) {
            // Гость уходит на регистрацию — само добавление засчитается уже после
            // возврата (?add_favorite=1), поэтому здесь отдельная цель-намерение.
            // Редирект — из колбэка, иначе навигация обрывает отправку цели.
            reachGoal('test_favorite_auth_required', { testId }, () => window.location.assign(authUrl));
            return;
        }
        // Анимируем по НАМЕРЕНИЮ пользователя (направление — по текущему состоянию),
        // а не по факту смены стора: тоггл может оптимистично откатиться при ошибке.
        if (isFavorite) {
            setBreakNonce((n) => n + 1); // убираем → «разбитое сердце»
            reachGoal('test_favorite_remove', { testId });
        } else {
            setBeatNonce((n) => n + 1); // добавляем → «сердцебиение»
            showFavoriteToastOncePerSession();
            reachGoal('test_favorite_add', { testId, source: 'click' });
        }
        dispatch(toggleTestFavorite(testId));
    };

    return (
        <>
            <FavoriteHeartButton
                iconColor={isFavorite ? 'error' : 'white'}
                pressed={isFavorite}
                label={label}
                onClick={handleClick}
                beatNonce={beatNonce}
                breakNonce={breakNonce}
            />

            {toastVisible && (
                <Snackbar autoHide onClose={() => setToastVisible(false)}>
                    <FlexContainer centeredContent centeredText verticalCentered gap="2">
                        <TextS>{t('favorite.added.text')}</TextS>
                        <Button autoWidth light small href={favoritesUrl}>
                            {t('favorite.added.cta')}
                        </Button>
                    </FlexContainer>
                </Snackbar>
            )}
        </>
    );
};

export default TestFavoriteButton;
